import { httpsCallable } from 'firebase/functions';
import type { Timestamp } from 'firebase/firestore';
import { functions } from '../firebase';

export interface JanitorInfo {
  uid: string;
  name: string;
  email: string;
  /** Library changes made today (ET). */
  today: number;
}

/** One row of the technique_edits log. */
export interface EditEntry {
  id: string;
  uid: string;
  byName: string;
  at: Date | null;
  /** What physically happened. A revert is one of these too, flagged by revertOf. */
  kind: 'edit' | 'archive' | 'restore';
  techniqueId: string;
  techniqueName: string | null;
  /** Changed fields only (edits). */
  before?: Record<string, unknown>;
  after?: Record<string, unknown>;
  revertOf?: string;
  revertedBy?: string;
  revertedByName?: string;
}

export function mapEditEntry(id: string, d: Record<string, unknown>): EditEntry {
  return {
    id,
    uid: d.uid as string,
    byName: (d.byName as string) || (d.uid as string),
    at: (d.at as Timestamp | undefined)?.toDate?.() ?? null,
    kind: d.kind as EditEntry['kind'],
    techniqueId: d.techniqueId as string,
    techniqueName: (d.techniqueName as string) || null,
    before: d.before as Record<string, unknown> | undefined,
    after: d.after as Record<string, unknown> | undefined,
    revertOf: d.revertOf as string | undefined,
    revertedBy: d.revertedBy as string | undefined,
    revertedByName: d.revertedByName as string | undefined,
  };
}

// Admin only.
export async function listJanitors(): Promise<JanitorInfo[]> {
  const fn = httpsCallable<Record<string, never>, { janitors: JanitorInfo[] }>(functions, 'listJanitors');
  return (await fn({})).data.janitors;
}

// Admin only. Grant or revoke by email (or uid, for rows already listed).
export async function setJanitor(target: { email?: string; uid?: string }, janitor: boolean): Promise<JanitorInfo> {
  const fn = httpsCallable<{ email?: string; uid?: string; janitor: boolean }, JanitorInfo>(functions, 'setJanitor');
  return (await fn({ ...target, janitor })).data;
}

// Admin only. Revokes, then reverts everything they did (newest first).
export async function undoJanitor(uid: string): Promise<{ reverted: number; skipped: { id: string; technique: string; reason: string }[] }> {
  const fn = httpsCallable<{ uid: string }, { reverted: number; skipped: { id: string; technique: string; reason: string }[] }>(
    functions, 'undoJanitor', { timeout: 540_000 },
  );
  return (await fn({ uid })).data;
}

// Admin or janitor.
export async function revertEdit(entryId: string): Promise<void> {
  await httpsCallable(functions, 'revertEdit')({ entryId });
}

export interface UserRow {
  uid: string;
  name: string;
  email: string;
  created: number | null;
  /** null = never had access. */
  plan: 'pro' | 'trial' | 'code' | 'expired' | null;
  /** When access ends (ms) — period end, trial end or gift expiry. null when unknown / no access. */
  until: number | null;
  /** Gift plan only: total length in days, redemption to expiry. */
  giftDays: number | null;
  /** Set to lapse at `until` instead of renewing. */
  cancelled: boolean;
  janitor: boolean;
  /** Library changes made today (ET); only meaningful for janitors. */
  today: number;
}

// Admin only. Every account, for the Manage Users list.
export async function listUsers(): Promise<UserRow[]> {
  const fn = httpsCallable<Record<string, never>, { users: UserRow[] }>(functions, 'listUsers');
  return (await fn({})).data.users;
}
